import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { Participant, Receipt } from "@/api/types";
import { useTrip } from "@/hooks/useTrip";
import { useReceipts, useDeleteReceipt, useRetryParse } from "@/hooks/useReceipts";
import { useParticipants } from "@/hooks/useParticipants";
import { PageShell } from "@/components/PageShell";
import { TripHeader } from "@/components/TripHeader";
import { useShareTrip } from "@/hooks/useShareTrip";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/Button";
import { FAB } from "@/components/FAB";
import { AddReceiptMenu } from "@/components/AddReceiptMenu";
import { ClosedTripNotice } from "@/components/ClosedTripNotice";
import { ReceiptCard } from "@/components/ReceiptCard";
import { AttentionReceiptRow } from "@/components/AttentionReceiptRow";
import { needsAttention } from "@/utils/receiptReadiness";
import { participantLabel } from "@/utils/avatar";
import { pluralReceipts } from "@/utils/format";
import "./receipts-list.css";

function groupByDate(
  receipts: ReadonlyArray<Receipt>,
): Array<{ key: string; label: string; items: Receipt[] }> {
  const groups = new Map<string, Receipt[]>();
  for (const r of receipts) {
    const d = new Date(r.created_at);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const arr = groups.get(key) ?? [];
    arr.push(r);
    groups.set(key, arr);
  }
  return [...groups.entries()].map(([key, items]) => {
    const d = new Date(items[0]!.created_at);
    const months = [
      "СІЧНЯ",
      "ЛЮТОГО",
      "БЕРЕЗНЯ",
      "КВІТНЯ",
      "ТРАВНЯ",
      "ЧЕРВНЯ",
      "ЛИПНЯ",
      "СЕРПНЯ",
      "ВЕРЕСНЯ",
      "ЖОВТНЯ",
      "ЛИСТОПАДА",
      "ГРУДНЯ",
    ];
    return { key, label: `${d.getDate()} ${months[d.getMonth()]}`, items };
  });
}

function paidByLabelFor(
  receipt: Receipt,
  participants: ReadonlyArray<Participant>,
): string | undefined {
  const p = participants.find(
    (x) => x.participant_id === receipt.paid_by_participant_id,
  );
  return p ? `${participantLabel(p)} сплатив(ла)` : undefined;
}

export function ReceiptsListScreen() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterAttention = searchParams.get("filter") === "attention";
  const [menuOpen, setMenuOpen] = useState(false);

  const tripQ = useTrip(slug);
  const handleShare = useShareTrip(tripQ.data);
  const receiptsQ = useReceipts(slug);
  const participantsQ = useParticipants(slug);
  const deleteReceipt = useDeleteReceipt(slug);
  const retryParse = useRetryParse(slug);

  const allReceipts = useMemo(
    () => receiptsQ.data?.receipts ?? [],
    [receiptsQ.data],
  );
  const attentionReceipts = useMemo(
    () => allReceipts.filter(needsAttention),
    [allReceipts],
  );
  const grouped = useMemo(
    () => groupByDate(allReceipts),
    [allReceipts],
  );

  if (tripQ.isLoading || !tripQ.data) {
    return (
      <PageShell>
        <Skeleton variant="block" width="100%" height={200} />
      </PageShell>
    );
  }
  const trip = tripQ.data;
  const participants = participantsQ.data?.participants ?? [];
  // Add-receipt is Open-only (AC-06.7): on a closed trip the FAB is hidden
  // and a <ClosedTripNotice> explains the read-only state.
  const isClosed = trip.status === "closed";
  const uploadPath = `/t/${trip.trip_id}/receipts/new/upload`;
  const manualPath = `/t/${trip.trip_id}/receipts/new`;

  function goPhoto(): void {
    setMenuOpen(false);
    navigate(uploadPath);
  }
  function goManual(): void {
    setMenuOpen(false);
    navigate(manualPath);
  }

  function clearFilter(): void {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next, { replace: true });
  }

  // Filtered "потребують уваги" view (receipts-list.md): the landing target of
  // the <IncompleteReceiptsNotice> link and the close gate's «Спершу
  // завершити». It is a filtered VIEW (not a new per-receipt marker) — the
  // existing <ReceiptStatusBadge> + soft hint name each reason. Each row has a
  // resolution path (open / retry / delete) so the gate is never a dead-end.
  if (filterAttention) {
    return (
      <PageShell header={<TripHeader trip={trip} onShare={handleShare} />}>
        {receiptsQ.isLoading ? (
          <Skeleton variant="block" width="100%" height={120} />
        ) : attentionReceipts.length === 0 ? (
          // Filter set emptied while open → can now close (receipts-list.md).
          <div className="wp-rcpts">
            <div className="wp-attn-filter" data-testid="attention-filter-header">
              <span className="wp-attn-filter__count">Потребують уваги · 0</span>
              <button
                type="button"
                className="wp-attn-filter__clear"
                onClick={clearFilter}
              >
                Усі чеки ✕
              </button>
            </div>
            <EmptyState
              icon={<span aria-hidden="true">✓</span>}
              title="Усе враховано"
              description="Тепер можна закрити поїздку."
              secondaryAction={
                <Link to={`/t/${trip.trip_id}/settle-up`}>До розрахунку</Link>
              }
            />
          </div>
        ) : (
          <div className="wp-rcpts">
            <div className="wp-attn-filter" data-testid="attention-filter-header">
              <span className="wp-attn-filter__count">
                Потребують уваги · {attentionReceipts.length}
              </span>
              <button
                type="button"
                className="wp-attn-filter__clear"
                onClick={clearFilter}
                data-testid="attention-filter-clear"
              >
                Усі чеки ✕
              </button>
            </div>
            <ol>
              {attentionReceipts.map((r) => (
                <AttentionReceiptRow
                  key={r.receipt_id}
                  tripSlug={trip.trip_id}
                  receipt={r}
                  paidByLabel={paidByLabelFor(r, participants)}
                  onRetry={
                    r.status === "parse_failed"
                      ? () => retryParse.mutate(r.receipt_id)
                      : undefined
                  }
                  retrying={
                    retryParse.isPending && retryParse.variables === r.receipt_id
                  }
                  onDelete={() => deleteReceipt.mutate(r.receipt_id)}
                  deleting={
                    deleteReceipt.isPending &&
                    deleteReceipt.variables === r.receipt_id
                  }
                />
              ))}
            </ol>
          </div>
        )}
      </PageShell>
    );
  }

  return (
    <PageShell header={<TripHeader trip={trip} onShare={handleShare} />}>
      {receiptsQ.isLoading ? (
        <Skeleton variant="block" width="100%" height={120} />
      ) : grouped.length === 0 ? (
        isClosed ? (
          // Closed trip, zero receipts (AC-19.3): no dead add CTA.
          <>
            <ClosedTripNotice variant="compact" />
            <EmptyState
              icon={<span aria-hidden="true">🔒</span>}
              title="Чеків немає"
              description="Поїздку закрито без жодного чека. Відкрий її в налаштуваннях, щоб додати перший."
              secondaryAction={
                <Link to={`/t/${trip.trip_id}/settings`}>Налаштування</Link>
              }
            />
          </>
        ) : (
          // Open trip, zero receipts (AC-20.1 / wireframe §A.2): the two add
          // paths render as explicit stacked buttons — photo (primary) +
          // manual (secondary) — so a first-time visitor sees manual entry
          // without discovering the FAB menu. The FAB is hidden here (the
          // buttons are the affordance); it returns once ≥1 receipt exists.
          <EmptyState
            icon={<span aria-hidden="true">🧾</span>}
            title="Ще немає чеків"
            description="Сфотографуй чек — AI розбере його на позиції. Або додай витрату вручну."
            primaryAction={
              <Button size="lg" fullWidth onClick={goPhoto} data-testid="empty-add-photo">
                Сфотографувати чек
              </Button>
            }
            secondaryAction={
              <Button
                variant="secondary"
                size="lg"
                fullWidth
                onClick={goManual}
                data-testid="empty-add-manual"
              >
                Додати вручну
              </Button>
            }
          />
        )
      ) : (
        <div className="wp-rcpts">
          {isClosed ? <ClosedTripNotice /> : null}
          {attentionReceipts.length > 0 ? (
            <Link
              to={`/t/${trip.trip_id}/receipts?filter=attention`}
              className="wp-attn-filter wp-attn-filter--cta"
              data-testid="attention-filter-cta"
            >
              <span className="wp-attn-filter__count">
                Потребують уваги · {pluralReceipts(attentionReceipts.length)}
              </span>
              <span aria-hidden="true">→</span>
            </Link>
          ) : null}
          {grouped.map((g) => (
            <section key={g.key}>
              <h2 className="wp-rcpts__overline">{g.label}</h2>
              <ol>
                {g.items.map((r) => (
                  <ReceiptCard
                    key={r.receipt_id}
                    tripSlug={trip.trip_id}
                    receipt={r}
                    paidBy={participants.find(
                      (p) => p.participant_id === r.paid_by_participant_id,
                    )}
                  />
                ))}
              </ol>
            </section>
          ))}
        </div>
      )}
      {/* FAB is hidden on a closed trip (AC-06.7) and on the zero-receipt
          empty state (the two explicit buttons are the affordance, §A.2). It
          opens the <AddReceiptMenu> chooser instead of routing to camera. */}
      {!isClosed && grouped.length > 0 ? (
        <FAB
          onClick={() => setMenuOpen(true)}
          ariaLabel="Додати чек"
          ariaHasPopup
          ariaExpanded={menuOpen}
        />
      ) : null}
      <AddReceiptMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onPickPhoto={goPhoto}
        onPickManual={goManual}
      />
    </PageShell>
  );
}
